import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { GetLanguageString } from "../../helper/Components";
import {
  LoadingSpinner,
  convertToDateString,
} from "../../../constants/const";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom";
import { GetCustomStyles, GetSelectedLanguage } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import swal from "sweetalert";
import DatePicker from "react-datepicker";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import Select from "react-select";

import { getMinMaxDates } from "../fiscalYearHelper";
import ShareTransferContextPanel, {
  getShareMaxTransferable,
} from "../ShareTransferContextPanel";
import ShareDateExtensionModal from "../ShareDateExtensionModal";

const HalqaShare = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));

  const [loading, setLoading] = useState({
    submit: false,
    calculate: false,
  });

  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);

  const getDefaultShareMonth = () => {
    const today = new Date();
    return today.getDate() <= 20
      ? new Date(today.getFullYear(), today.getMonth() - 1, 1)
      : new Date(today.getFullYear(), today.getMonth(), 1);
  };

  const getShareMonthBounds = (shareMonthDate) => {
    if (!shareMonthDate) return { minDate: null, maxDate: new Date() };
    const year = shareMonthDate.getFullYear();
    const month = shareMonthDate.getMonth();
    const minDate = new Date(year, month, 1);
    const today = new Date();
    return { minDate, maxDate: today };
  };

  const defaultShareMonth = getDefaultShareMonth();
  const defaultProcessedDate = new Date();

  const [formData, setFormData] = useState({
    share_month: defaultShareMonth,
    processed_date: defaultProcessedDate,
    amount: "",
    memo: "",
  });

  const [errors, setErrors] = useState({});

  const { minDate: fiscalMinDate } = getMinMaxDates();
  const getLatestShareMonthMaxDate = () => {
    const today = new Date();
    return today.getDate() <= 20
      ? new Date(today.getFullYear(), today.getMonth(), 0)
      : today;
  };
  const latestShareMonthMaxDate = getLatestShareMonthMaxDate();
  const { minDate, maxDate } = getShareMonthBounds(formData.share_month);

  const checkSubmissionStatus = useCallback(() => {
    if (!loggedInUnitId) return;

    ApiFinanceLocal()
      .GetHalqaShareSubmissionStatus(loggedInUnitId)
      .then((res) => {
        const status = res.data;
        setSubmissionStatus(status);
        if (status && !status.canSubmit) {
          let message = status.message;
          if (status.status === "pending") {
            message = t("funds_halqa_share_pending_tooltip", "Halqa Share request is currently pending approval.");
          } else if (status.status === "completed") {
            message = t("funds_halqa_share_completed_tooltip", "Halqa Share for this month has already been submitted and approved.");
          } else if (status.status === "extension_pending") {
            message = t("funds_halqa_share_extension_pending_tooltip", "Date extension request pending approval.");
          } else if (status.status === "cutoff_elapsed") {
            message = t("funds_halqa_share_cutoff_elapsed_tooltip", {
              day: status.cutoffDay || 7,
              defaultValue: `Halqa Share submission for this month closed on the ${status.cutoffDay || 7}th.`,
            });
          }

          if (status.status === "cutoff_elapsed" || status.status === "extension_expired") {
            swal({
              title: t("common_warning", "Warning"),
              text: message || t("funds_halqa_share_submission_disabled_warning", "Halqa Share submission is not available at this time."),
              icon: "warning",
              buttons: {
                cancel: t("common_cancel", "Cancel"),
                confirm: t("funds_request_date_extension_btn", "Request Extension"),
              },
            }).then((confirmed) => {
              if (confirmed) {
                setIsExtensionModalOpen(true);
              } else {
                history.push("/funds/quickManageFund");
              }
            });
          } else {
            swal({
              title: t("common_warning", "Warning"),
              text: message || t("funds_halqa_share_submission_disabled_warning", "Halqa Share submission is not available at this time."),
              icon: "warning",
              button: t("common_ok", "OK"),
            }).then(() => {
              history.push("/funds/quickManageFund");
            });
          }
        }
      })
      .catch((err) => {
        console.error("Error checking Halqa share submission status:", err);
      });
  }, [loggedInUnitId, history, t]);

  useEffect(() => {
    checkSubmissionStatus();
  }, [checkSubmissionStatus]);

  useEffect(() => {
    ApiFinanceLocal()
      .GetFinanceBankAccounts(loggedInUnitId)
      .then((res) => {
        const filtered = (res.data || []).filter(
          (account) =>
            account?.label1 === "cash_in_hand_account" ||
            account?.label1 === "amanat_account",
        );
        setBankAccounts(filtered);
        if (filtered.length > 0) {
          const cashAcc = filtered.find((a) => a.label1 === "cash_in_hand_account");
          setSelectedBankAccount(cashAcc || filtered[0]);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [loggedInUnitId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleShareMonthChange = (date) => {
    const selectedMonth = date ?? getDefaultShareMonth();
    const bounds = getShareMonthBounds(selectedMonth);
    let updatedProcessedDate = formData.processed_date;
    if (updatedProcessedDate < bounds.minDate || updatedProcessedDate > bounds.maxDate) {
      const today = new Date();
      if (today >= bounds.minDate && today <= bounds.maxDate) {
        updatedProcessedDate = today;
      } else {
        updatedProcessedDate = bounds.maxDate;
      }
    }
    setFormData((prev) => ({
      ...prev,
      share_month: selectedMonth,
      processed_date: updatedProcessedDate,
    }));
    setErrors((prev) => ({ ...prev, share_month: "", processed_date: "" }));
  };

  const [shareContext, setShareContext] = useState(null);
  const calculatedAmount = Number(shareContext?.minimumShare) || 0;
  const sourceAccountType =
    selectedBankAccount?.label1 === "amanat_account" ? "amanat" : "cash";

  useEffect(() => {
    if (!formData.share_month) return;

    setLoading((prev) => ({ ...prev, calculate: true }));
    const formattedDate = convertToDateString(formData.share_month);

    ApiFinanceLocal()
      .GetHalqaShareContext(loggedInUnitId, formattedDate)
      .then((res) => {
        const context = res.data || {};
        setShareContext(context);
        setFormData((prev) => ({
          ...prev,
          amount: (context.minimumShare ?? 0).toString(),
        }));
        setLoading((prev) => ({ ...prev, calculate: false }));
      })
      .catch((err) => {
        console.error(err);
        toast.error(t("funds_halqa_share_calculation_failed"));
        setLoading((prev) => ({ ...prev, calculate: false }));
      });
  }, [formData.share_month, loggedInUnitId, t]);

  const validate = () => {
    const temp = {};
    let isValid = true;

    if (!formData.share_month) {
      temp.share_month = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.processed_date) {
      temp.processed_date = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.amount || formData.amount.toString().trim() === "") {
      temp.amount = t("common_field_required_error");
      isValid = false;
    } else {
      const parsedAmount = parseFloat(formData.amount);
      const maxTransferable = getShareMaxTransferable(shareContext, sourceAccountType);
      const totalInfaq = Number(shareContext?.totalInfaq) || 0;
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        temp.amount = t("amount_must_be_greater_than_zero");
        isValid = false;
      } else if (shareContext?.hasPendingRequest) {
        temp.amount = t("share_request_already_pending");
        isValid = false;
      } else if (parsedAmount > totalInfaq) {
        temp.amount = t("share_amount_exceeds_total_infaq");
        isValid = false;
      } else if (parsedAmount > maxTransferable) {
        temp.amount = t("share_amount_exceeds_account_balance");
        isValid = false;
      }
    }

    if (!formData.memo || formData.memo.toString().trim() === "") {
      temp.memo = t("common_field_required_error");
      isValid = false;
    }

    setErrors(temp);
    return isValid;
  };

  const formatLocalDateString = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!validate()) {
      return;
    }

    const executeSubmit = () => {
      let finalMemo = formData.memo ? formData.memo.trim() : "";
      if (formData.share_month) {
        const monthName = formData.share_month.toLocaleString("en-US", { month: "long" });
        const year = formData.share_month.getFullYear();
        if (!finalMemo.toLowerCase().includes(monthName.toLowerCase())) {
          finalMemo = finalMemo ? `${finalMemo} (${monthName} ${year})` : `${monthName} ${year}`;
        }
      }

      const payload = {
        date: formatLocalDateString(formData.processed_date),
        share_month: formatLocalDateString(formData.share_month),
        amount: parseFloat(formData.amount),
        memo: finalMemo,
        unitId: loggedInUnitId,
        isRequest: true,
        source_account_type: selectedBankAccount?.label1 === "amanat_account" ? "amanat" : "cash",
      };

      setLoading((prev) => ({ ...prev, submit: true }));

      ApiFinanceLocal()
        .AddHalqaShareExpense(payload)
        .then((res) => {
          const data = res.data;
          if (data?.statusCode === 200) {
            let msg =
              (data.message && t(data.message) !== data.message ? t(data.message) : "") ||
              (data.message === "finance_halqa_share_amanat_requested"
                ? t("finance_halqa_share_amanat_requested")
                : t("finance_halqa_share_requested"));
            swal({
              title: "Success",
              text: msg,
              icon: "success",
              buttons: "OK",
            }).then(() => {
              history.goBack();
            });
            return;
          }

          swal({
            title: "Error",
            text: data?.message || "Failed to add Halqa Share expense",
            icon: "error",
            buttons: "OK",
          });
          setLoading((prev) => ({ ...prev, submit: false }));
        })
        .catch((err) => {
          console.error(err);
          const raw = err?.response?.data;
          const message = typeof raw === "string" ? raw : raw?.message;
          swal({
            title: "Error",
            text: message && t(message) !== message ? t(message) : "Unable to connect to server. Please try again.",
            icon: "error",
            buttons: "OK",
          });
          setLoading((prev) => ({ ...prev, submit: false }));
        });
    };

    executeSubmit();
  };

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <ToastContainer
        autoClose={3000}
        bodyClassName={GetSelectedLanguage()}
        closeOnClick
        pauseOnHover={false}
        draggable
      />
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>{<GetLanguageString props="funds_halqa_share" />}</h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading.submit ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              {submissionStatus?.extendedUntilDate && (
                <div
                  className="d-flex align-items-center mb-3"
                  style={{
                    backgroundColor: "#F0FDF4",
                    border: "1px solid #BBF7D0",
                    borderLeft: "4px solid #10B981",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    color: "#065F46",
                    fontSize: "13px",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: "#DCFCE7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                      flexShrink: 0,
                    }}
                  >
                    <i className="fa fa-calendar-check-o" style={{ color: "#16A34A", fontSize: "14px" }}></i>
                  </div>
                  <div>
                    <strong style={{ color: "#065F46" }}>
                      {t("funds_date_extension_extended_until", "Extended until:")}{" "}
                    </strong>
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#166534",
                      }}
                    >
                      {moment(submissionStatus.extendedUntilDate).isValid()
                        ? moment(submissionStatus.extendedUntilDate).locale("en").format("MMMM Do, YYYY")
                        : submissionStatus.extendedUntilDate}
                    </span>
                  </div>
                </div>
              )}
              <ShareTransferContextPanel
                context={shareContext}
                loading={loading.calculate}
                minimumLabelKey="share_minimum_halqa"
              />
              <Row>
                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_share_month" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Select month"
                      className="form-control no-drag"
                      selected={formData.share_month}
                      onChange={handleShareMonthChange}
                      showMonthYearPicker
                      minDate={fiscalMinDate}
                      maxDate={latestShareMonthMaxDate}
                      dateFormat="MMMM yyyy"
                    />
                    {errors.share_month && <h4 className="text-error">{errors.share_month}</h4>}
                  </FormGroup>
                </Col>

                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_processed_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Select date"
                      className="form-control no-drag"
                      selected={formData.processed_date}
                      onChange={(date) => handleChange("processed_date", date ?? new Date())}
                      minDate={minDate}
                      maxDate={maxDate}
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.processed_date && <h4 className="text-error">{errors.processed_date}</h4>}
                  </FormGroup>
                </Col>

                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="account_type" />}
                    </Label>
                    <Select
                      options={bankAccounts}
                      value={selectedBankAccount}
                      className="basic-single no-drag"
                      classNamePrefix="select"
                      onChange={(option) => setSelectedBankAccount(option)}
                    />
                  </FormGroup>
                </Col>

                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_add_fund_amount" />}
                    </Label>
                    <Input
                      name="amount"
                      type="text"
                      value={formData.amount}
                      onChange={(e) => handleChange("amount", e.target.value)}
                      className="no-drag"
                      invalid={!!errors.amount}
                      disabled={loading.calculate}
                    />
                    {loading.calculate ? (
                      <small className="text-muted d-block mt-1">Calculating share...</small>
                    ) : (
                      calculatedAmount > 0 && parseFloat(formData.amount) > 0 && parseFloat(formData.amount) < calculatedAmount && (
                        <div
                          style={{
                            backgroundColor: "#FEF3C7",
                            border: "1px solid #F59E0B",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            marginTop: "6px",
                            color: "#92400E",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          <i className="fa fa-exclamation-triangle mr-1" style={{ color: "#D97706", marginRight: "4px" }}></i>
                          Entered amount is less than calculated share (2/3 Halqa Share: {calculatedAmount.toLocaleString()})
                        </div>
                      )
                    )}
                    {errors.amount && <h4 className="text-error no-drag">{errors.amount}</h4>}
                  </FormGroup>
                </Col>

                <Col>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="transactions_memo" />}
                    </Label>
                    <Input
                      name="memo"
                      type="text"
                      value={formData.memo}
                      onChange={(e) => handleChange("memo", e.target.value)}
                      className="no-drag"
                      invalid={!!errors.memo}
                    />
                    {errors.memo && <h4 className="text-error no-drag">{errors.memo}</h4>}
                  </FormGroup>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  <Button
                    type="submit"
                    color="primary"
                    disabled={loading.submit || loading.calculate}
                  >
                    {<GetLanguageString props="funds_submit" />}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
      <ShareDateExtensionModal
        isOpen={isExtensionModalOpen}
        toggle={() => {
          setIsExtensionModalOpen(false);
          history.push("/funds/quickManageFund");
        }}
        unitId={loggedInUnitId}
        shareType="halqa"
        shareMonthDate={formData.share_month}
        onSuccess={() => {
          checkSubmissionStatus();
        }}
      />
    </div>
  );
};

export default withRouter(HalqaShare);
